// EXP1-S6 (docs/build-briefs/EXP1-S6-google-sheet-export.md; owner ruling 2026-10-13: "users should connect their own
// Google Account. Being prompted to do so the first time they try to export to a google sheet."). The browser half of
// the Google Sheet export.
//
// The Google access token is held in ONE module variable below. It is never written to storage, a URL, a log or a
// request to our own server (the server renders the workbook and knows nothing of Google). resetGoogleSheets() drops it
// and is called from invalidate() on sign-out, so a shared tablet never carries one person's Google access to the next.
//
// The token request must run inside the tap that asked for it, or the browser blocks Google's sign-in window. So the
// Google Identity Services script is loaded when the menu opens, and ensureGoogleAccessToken() starts its request
// before its first await.

import { getExportOptions } from "./api.ts";
import { fileNameFor, type GateExportFormat } from "./exportMenuModel.ts";
import {
  DRIVE_UPLOAD_URL,
  GOOGLE_DRIVE_FILE_SCOPE,
  GOOGLE_IDENTITY_SCRIPT_URL,
  GOOGLE_MESSAGES,
  buildDriveMultipart,
  parseDriveFile,
  readTokenResponse,
  tokenFailureReason,
  type TokenResponse,
} from "./googleSheetsModel.ts";

interface TokenClient {
  requestAccessToken: (overrides?: { prompt?: string }) => void;
}
interface GoogleIdentity {
  accounts: {
    oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: TokenResponse) => void;
        error_callback: (error: { type?: string }) => void;
      }) => TokenClient;
    };
  };
}

export type AccessOutcome = { ok: true } | { ok: false; message: string };
export type UploadOutcome = { ok: true; webViewLink: string } | { ok: false; message: string };

/** undefined: not asked yet this session. null: asked, and the export is off. */
let clientId: string | null | undefined;
let configLoad: Promise<void> | undefined;
let identityScript: Promise<boolean> | undefined;
let tokenClient: TokenClient | undefined;
let token: { value: string; expiresAtMs: number } | undefined;
let tokenRequest: Promise<AccessOutcome> | undefined;
/** Hands the in-flight request's result to the token client's callbacks. */
let settleRequest: ((outcome: AccessOutcome) => void) | undefined;

export function googleSheetsEnabled(): boolean {
  return typeof clientId === "string";
}

export function hasGoogleAccessToken(): boolean {
  return token !== undefined && token.expiresAtMs > Date.now();
}

/** Asks our own server once per session whether a Google client id is configured. A failure leaves the item off. */
export function loadGoogleSheetsConfig(
  apiBaseUrl: string,
  tenantId: string,
  /** PAY2-S8: the franchisor console has no tenant; it asks its own session-scoped route for the same public client id. */
  loadOptions?: () => Promise<{
    status: number;
    body: { googleSheetsClientId?: string | null } | null;
  }>,
): Promise<void> {
  if (configLoad === undefined) {
    configLoad = (async () => {
      const result =
        loadOptions === undefined
          ? await getExportOptions(apiBaseUrl, tenantId)
          : await loadOptions().then((r) => ({ status: r.status, body: r.body ?? {} }));
      clientId =
        result.status === 200 && typeof result.body.googleSheetsClientId === "string"
          ? result.body.googleSheetsClientId
          : null;
    })().catch(() => {
      clientId = null;
    });
  }
  return configLoad;
}

/** Loads Google Identity Services once. Resolves false when the script cannot be loaded. */
export function preloadGoogleIdentity(): Promise<boolean> {
  if (identityScript === undefined) {
    identityScript = new Promise<boolean>((resolve) => {
      const script = document.createElement("script");
      script.src = GOOGLE_IDENTITY_SCRIPT_URL;
      script.async = true;
      script.addEventListener("load", () => resolve(true));
      script.addEventListener("error", () => {
        script.remove();
        identityScript = undefined;
        resolve(false);
      });
      document.head.append(script);
    });
  }
  return identityScript;
}

function identity(): GoogleIdentity | undefined {
  return (window as unknown as { google?: GoogleIdentity }).google;
}

function requestToken(prompt: string): Promise<AccessOutcome> {
  if (tokenRequest !== undefined) return tokenRequest;
  const id = clientId;
  if (typeof id !== "string") {
    return Promise.resolve({ ok: false, message: GOOGLE_MESSAGES.unavailable });
  }
  const request = new Promise<AccessOutcome>((resolve) => {
    settleRequest = resolve;
  }).finally(() => {
    tokenRequest = undefined;
    settleRequest = undefined;
  });
  tokenRequest = request;
  const start = (): void => {
    const google = identity();
    if (google === undefined) {
      settleRequest?.({ ok: false, message: GOOGLE_MESSAGES.unavailable });
      return;
    }
    tokenClient ??= google.accounts.oauth2.initTokenClient({
      client_id: id,
      scope: GOOGLE_DRIVE_FILE_SCOPE,
      callback: (response) => {
        const held = readTokenResponse(response, Date.now());
        if (held === null) {
          settleRequest?.({ ok: false, message: GOOGLE_MESSAGES.notFinished });
          return;
        }
        token = held;
        settleRequest?.({ ok: true });
      },
      error_callback: (error) => {
        settleRequest?.({
          ok: false,
          message:
            tokenFailureReason(error.type) === "blocked"
              ? GOOGLE_MESSAGES.blocked
              : GOOGLE_MESSAGES.notFinished,
        });
      },
    });
    tokenClient.requestAccessToken({ prompt });
  };
  if (identity() !== undefined) {
    // Still inside the tap that asked: the browser allows Google's window.
    start();
  } else {
    void preloadGoogleIdentity().then((loaded) => {
      if (loaded) start();
      else settleRequest?.({ ok: false, message: GOOGLE_MESSAGES.unavailable });
    });
  }
  return request;
}

/** Resolves ok at once when a live token is held; otherwise asks Google for one. The first time, Google asks the person
 * to choose and connect their account. Call it from the tap itself. */
export function ensureGoogleAccessToken(): Promise<AccessOutcome> {
  if (hasGoogleAccessToken()) return Promise.resolve({ ok: true });
  token = undefined;
  return requestToken(tokenClient === undefined ? "select_account" : "");
}

/** Uploads the workbook to the person's own Drive, converted to a Google Sheet. A rejected token (401) is dropped and
 * asked for once more. Nothing is reported as done unless Drive answered with the new file. */
export async function uploadWorkbookAsSheet(workbook: Blob, name: string): Promise<UploadOutcome> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (!hasGoogleAccessToken()) {
      const access = await requestToken("");
      if (!access.ok) return access;
    }
    const held = token;
    if (held === undefined) return { ok: false, message: GOOGLE_MESSAGES.notFinished };
    const boundary = `snipsip${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    const multipart = buildDriveMultipart(name, workbook, boundary);
    let response: Response;
    try {
      response = await fetch(DRIVE_UPLOAD_URL, {
        method: "POST",
        headers: { authorization: `Bearer ${held.value}`, "content-type": multipart.contentType },
        body: multipart.body,
      });
    } catch {
      return { ok: false, message: GOOGLE_MESSAGES.uploadFailed };
    }
    if (response.status === 401 && attempt === 0) {
      token = undefined;
      continue;
    }
    if (response.status !== 200) return { ok: false, message: GOOGLE_MESSAGES.uploadFailed };
    const file = parseDriveFile(await response.json().catch(() => null));
    return file === null
      ? { ok: false, message: GOOGLE_MESSAGES.uploadFailed }
      : { ok: true, webViewLink: file.webViewLink };
  }
  return { ok: false, message: GOOGLE_MESSAGES.uploadFailed };
}

/** Sign-out (admin.ts invalidate()): forget the token, the answer about the client id and any request in flight. */
export function resetGoogleSheets(): void {
  token = undefined;
  clientId = undefined;
  configLoad = undefined;
  tokenClient = undefined;
  settleRequest?.({ ok: false, message: GOOGLE_MESSAGES.notFinished });
}

/** The status line after an export file arrives: downloads it, or for a Google Sheet uploads it and opens the sheet. */
export async function deliverExportFile(
  status: HTMLElement,
  blob: Blob,
  stem: string,
  format: GateExportFormat,
): Promise<void> {
  status.replaceChildren();
  if (format !== "google_sheet") {
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileNameFor(stem, format);
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
    status.textContent = "Downloaded.";
    return;
  }
  status.textContent = GOOGLE_MESSAGES.creating;
  const outcome = await uploadWorkbookAsSheet(blob, stem);
  if (!outcome.ok) {
    status.textContent = outcome.message;
    return;
  }
  const tab = window.open(outcome.webViewLink, "_blank");
  if (tab !== null) {
    tab.opener = null;
    status.textContent = GOOGLE_MESSAGES.opened;
    return;
  }
  const link = document.createElement("a");
  link.href = outcome.webViewLink;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.textContent = "Open the sheet";
  link.dataset["testid"] = "google-sheet-open-link";
  status.append(`${GOOGLE_MESSAGES.popupBlocked} `, link);
}
